import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { v1 as uuidv1 } from 'uuid';

import { getChainPrivateApi } from '#/api/apiCoreSdk';
import { minutes, seconds } from '#/api/queries/utils';
import { getUnlockWalletSignatureTypedData } from '#/api/signatures';
import { displayNotification } from '#/components/layout/notifications/notificationsSdk';
import { signTypedDataWithModal } from '#/lib/ethereum/sign/signHashWithModal';
import { generateDelegatedKey } from '#/lib/ethereum/wallets/delegatedKeyWallet';
import { fullLogoutAndDispatch } from '#/reduxState/currentUser/currentUserUtils';
import { setDelegatedKey } from '#/reduxState/delegatedKey/authorizationReducer';
import { staticDispatch, useSelector } from '#/reduxState/store';
import { IWallet } from '#/reduxState/wallet/walletReducer';

export function useInvalidateDelegatedKeysQueries() {
  const queryClient = useQueryClient();

  const invalidateDelegatedKeys = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['delegated-keys'],
    });
    // queryClient.invalidateQueries({
    //   queryKey: ['delegated-key-data'],
    // });
  }, [queryClient]);

  return {
    invalidateDelegatedKeys,
  };
}

export function useQueryWalletDelegatedKeys() {
  const wallet = useSelector(state => state.wallet);
  const delegatedKey = useSelector(state => state.delegatedKey);
  const { walletAddress } = wallet;
  const { walletAddress: delegatedWalletAddress } = delegatedKey.authorization;

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- query result depends only on walletAddress; wallet/delegatedKey are used only for the expiry-logout side effect and would cause spurious refetches if added to the key
  return useQuery({
    queryKey: ['delegated-keys', walletAddress],
    queryFn: () =>
      getChainPrivateApi()
        .getInternalDelegatedKeys(walletAddress)
        .then(response => {
          const { keys } = response;
          if (keys && delegatedWalletAddress) {
            const result = keys.find(
              ({ delegatedKey }) =>
                delegatedKey.toLowerCase() ===
                delegatedWalletAddress.toLowerCase()
            );
            if (!result) {
              console.warn('Delegated Key not found or expired, must renew');
              setTimeout(() => {
                const previousWallet = { ...wallet };
                const isPersistentEngine =
                  delegatedKey.settings.engine === 'persistent';
                fullLogoutAndDispatch(wallet, delegatedKey.authorization);
                if (wallet.walletType) {
                  displayNotification({
                    type: 'delegatedKeyExpired',
                    time: Date.now(),
                    walletType: previousWallet.walletType,
                    walletRdns: previousWallet.walletDiscoveryRdns,
                    walletRemember: isPersistentEngine,
                  });
                }
              });
            }
          }
          return response.keys.reverse();
        }),
    enabled: !!walletAddress,
    staleTime: seconds(30),
    refetchInterval: minutes(5),
  });
}

/**
 * Query for a single delegated keys data - gets open orders for the key, if any
 * @param delegatedKey
 */
export function useQueryWalletDelegatedKeyData(delegatedKey?: string) {
  const { walletAddress } = useSelector(state => state.wallet);

  return useQuery({
    queryKey: ['delegated-key-data', walletAddress, delegatedKey],
    queryFn:
      !delegatedKey || !walletAddress
        ? skipToken
        : () =>
            getChainPrivateApi().getDelegatedKey(walletAddress, delegatedKey),
    staleTime: minutes(1),
  });
}

export function useMutateRemoveDelegatedKey() {
  const queryClient = useQueryClient();
  const walletAddress = useSelector(state => state.wallet.walletAddress);

  return useMutation({
    mutationKey: ['deleteDelegatedKey'],
    onSuccess: () => {
      // cancel current queries for keys
      queryClient.cancelQueries({
        queryKey: ['delegated-keys', walletAddress],
      });
      // if anyone cares - refetch the staking status and update screen
      queryClient.invalidateQueries({
        queryKey: ['delegated-keys', walletAddress],
      });
    },

    mutationFn: async function revokeDelegatedKey(
      params: $Debug<
        Omit<
          Parameters<
            ReturnType<typeof getChainPrivateApi>['revokeDelegatedKey']
          >[0],
          'wallet'
        >
      >
    ) {
      console.log('Mutate with ', walletAddress);
      if (!walletAddress) {
        throw new Error('Failed to delegate to host name, wallet not defined');
      }
      return getChainPrivateApi().revokeDelegatedKey({
        ...params,
        wallet: walletAddress,
      });
    },
  });
}

async function associateDelegatedKey(
  wallet: IWallet,
  {
    delegatedKey,
    delegatedName,
    nonce: providedNonce,
    signTitle,
  }: {
    delegatedKey: string;
    delegatedName?: string;
    nonce?: string;
    signTitle: string;
  }
) {
  if (!wallet.walletType) {
    throw new Error('Unknown wallet type');
  }
  if (!wallet.walletAddress) {
    throw new Error('Failed to authorize session key, wallet not defined');
  }
  const api = getChainPrivateApi();
  const nonce = providedNonce ?? uuidv1();

  const signature = await signTypedDataWithModal(
    wallet,
    undefined,
    await getUnlockWalletSignatureTypedData({
      nonce,
      delegatedKey,
    }),
    signTitle
  );
  await api.internalAssociateWallet({
    walletAddress: wallet.walletAddress,
    walletType: wallet.walletType,
    delegatedKey,
    delegatedName,
    signature,
    nonce,
  });
  return nonce;
}

async function createDelegatedKey(wallet: IWallet, delegatedName?: string) {
  const nonce = uuidv1();
  const delegatedKeyParams = await generateDelegatedKey(nonce);
  const { delegatedHDWallet } = delegatedKeyParams;

  await associateDelegatedKey(wallet, {
    delegatedKey: delegatedHDWallet.address,
    delegatedName,
    nonce,
    signTitle: 'Please sign to create the Delegated Key',
  });
  return {
    delegatedNonce: nonce,
    delegatedWalletPrivateKey: delegatedHDWallet.privateKey,
  };
}

export function useMutateCreateDelegatedKey() {
  const wallet = useSelector(state => state.wallet);

  return useMutation({
    mutationKey: ['walletAssociateDelegatedKey'],
    mutationFn: async function revokeDelegatedKeyMutation({
      delegatedName,
    }: {
      delegatedName?: string;
    }) {
      return createDelegatedKey(wallet, delegatedName);
    },
  });
}

export function useMutateReauthorizeDelegatedKey() {
  const wallet = useSelector(state => state.wallet);
  const currentDelegatedKey = useSelector(
    state => state.delegatedKey.authorization
  );
  const delegatedKeySettings = useSelector(
    state => state.delegatedKey.settings
  );
  const { invalidateDelegatedKeys } = useInvalidateDelegatedKeysQueries();

  return useMutation({
    mutationKey: ['walletReauthorizeDelegatedKey'],
    onSuccess: (_data, { delegatedKey }) => {
      invalidateDelegatedKeys();
      if (
        delegatedKeySettings.engine &&
        currentDelegatedKey.walletAddress &&
        currentDelegatedKey.walletAddress.toLowerCase() ===
          delegatedKey.toLowerCase()
      ) {
        staticDispatch(
          setDelegatedKey(delegatedKeySettings.engine, {
            ...currentDelegatedKey,
            createdTime: Date.now(),
            lastRenewNoticeTime: undefined,
          })
        );
      }
    },
    mutationFn: async function reauthorizeDelegatedKeyMutation({
      delegatedKey,
      delegatedName,
    }: {
      delegatedKey: string;
      delegatedName?: string;
    }) {
      await associateDelegatedKey(wallet, {
        delegatedKey,
        delegatedName,
        signTitle: 'Please sign to reauthorize the Session Key',
      });
    },
  });
}
